import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2, ExternalLink } from "lucide-react";

type Filter = "all" | "active" | "inactive" | "low" | "medium" | "high" | "critical";

export default function CompetitorsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filter, setFilter] = useState<Filter>("all");
  const [filtersOpen, setFiltersOpen] = useState(true);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (filter === "active")   p.isActive = true;
    if (filter === "inactive") p.isActive = false;
    if (filter === "low")      p.threatLevel = "LOW";
    if (filter === "medium")   p.threatLevel = "MEDIUM";
    if (filter === "high")     p.threatLevel = "HIGH";
    if (filter === "critical") p.threatLevel = "CRITICAL";
    return p;
  }, [filter]);

  const { data, isLoading } = useList<any>("/competitors", { q, page, pageSize, sortBy, sortDir, ...params });
  const rows = data?.data ?? [];
  const total = data?.pagination?.total;

  async function del(id: string, name: string) {
    if (!confirm(`Delete competitor "${name}"?`)) return;
    await api.delete(`/competitors/${id}`);
    qc.invalidateQueries({ queryKey: ["/competitors"] });
  }

  const groups: FilterGroup[] = [
    {
      title: "Status",
      items: [
        { label: "All",      active: filter === "all",      onClick: () => setFilter("all"), count: total },
        { label: "Active",   active: filter === "active",   onClick: () => setFilter("active") },
        { label: "Inactive", active: filter === "inactive", onClick: () => setFilter("inactive") },
      ],
    },
    {
      title: "Threat level",
      items: [
        { label: "Low",      active: filter === "low",      onClick: () => setFilter("low") },
        { label: "Medium",   active: filter === "medium",   onClick: () => setFilter("medium") },
        { label: "High",     active: filter === "high",     onClick: () => setFilter("high") },
        { label: "Critical", active: filter === "critical", onClick: () => setFilter("critical") },
      ],
    },
  ];

  const threatVariant = (t: string) =>
    t === "CRITICAL" ? "destructive" :
    t === "HIGH"     ? "destructive" :
    t === "MEDIUM"   ? "default" : "secondary";

  return (
    <>
      <PageHeader
        title="Competitors"
        description="Companies you compete against — track threat level, strengths, weaknesses, and where they show up across accounts and deals."
        actions={<Button asChild><Link to="/competitors/new"><Plus className="mr-1 h-4 w-4"/>Create Competitor</Link></Button>}
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "name",      label: "Name" },
          { value: "createdAt", label: "Created" },
          { value: "threatLevel", label: "Threat level" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={<Input placeholder="Search competitors…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />}
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Website</Th>
                  <Th>Threat</Th>
                  <Th className="text-right">Accounts</Th>
                  <Th className="text-right">Deals</Th>
                  <Th>Owner</Th>
                  <Th>Active</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={8} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={8} className="py-12 text-center text-muted-foreground">No competitors yet.</Td></Tr>
                )}
                {rows.map((c: any) => (
                  <Tr key={c.id} className="group">
                    <Td><Link to={`/competitors/${c.id}`} className="font-medium hover:underline">{c.name}</Link></Td>
                    <Td>{c.website ? (
                      <a href={c.website} target="_blank" rel="noreferrer" className="text-muted-foreground hover:underline">
                        {new URL(c.website).hostname.replace(/^www\./, "")}
                      </a>
                    ) : <span className="text-muted-foreground">—</span>}</Td>
                    <Td><Badge variant={threatVariant(c.threatLevel) as any}>{c.threatLevel}</Badge></Td>
                    <Td className="text-right tabular-nums">{c._count?.accountLinks ?? 0}</Td>
                    <Td className="text-right tabular-nums">{c._count?.dealLinks ?? 0}</Td>
                    <Td className="text-muted-foreground">{c.owner?.name ?? "—"}</Td>
                    <Td>{c.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</Td>
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => del(c.id, c.name)} aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}
